import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, combineLatestWith, defer, firstValueFrom, from } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { HeaderService } from 'src/app/services/header.service';
import { BotModel } from 'src/app/models/bot';
import { CorpModel } from 'src/app/models/corp';
import { HierarchyElementModel } from 'src/app/models';
import { HierarchyElementsService, CorpsService, BotsService, NodesService } from 'src/app/services/firestore';
import { ClientEnvironmentService } from 'src/app/services/client-environment.service';

import { NodeModel } from 'src/app/models/node';
import FileSaver from 'file-saver';
import { TemplateChannels, TemplateModel } from 'src/app/models/template';
import { makeObjectFirebaseTimestampHumanReadable } from 'src/app/utils/date/date-utils';

type NodeExportKeys = keyof NodeModel | 'templateWeb' | 'templateFacebook';

@Component({
  selector: 'app-bot-export',
  templateUrl: './bot-export.component.html',
  styleUrls: ['./bot-export.component.scss'],
})
export class BotExportComponent implements OnInit, OnDestroy {
  private paramMapSubscription: Subscription;
  hierarchyElement: HierarchyElementModel;
  corp: CorpModel;
  bot: BotModel;
  nodes: NodeModel[] = [];
  exportAbleColumnsAsArray: Array<{
    key: string;
    label: string;
  }>;
  loading = true;

  exportAbleColumns: { [key in NodeExportKeys]?: string } = {
    id: 'ID',
    name: 'Label',
    action: 'Action',
    systemName: 'System Name',
    spec: 'Spec',
    templateWeb: 'Template (Web)',
    templateFacebook: 'Template (Facebook)',
    createdAt: 'Creation Date',
    updatedAt: 'Updated Date',
    inputData: 'Input Data',
    specTemplates: 'Spec Templates',
    live: 'Is Live',
    connections: 'Connections',
    x: 'X',
    y: 'Y',
    notes: 'Notes',
  };

  selectedColumns: Array<NodeExportKeys> = ['id', 'name', 'action', 'templateWeb', 'templateFacebook', 'createdAt'];

  constructor(
    private headerService: HeaderService,
    private route: ActivatedRoute,
    private corpsService: CorpsService,
    private botsService: BotsService,
    private nodesService: NodesService,
    private hierarchyElementsService: HierarchyElementsService,
    private authService: AuthService,
    private toaster: ToastrService,
    private clientEnvironmentService: ClientEnvironmentService,
  ) {
    this.exportAbleColumnsAsArray = Object.entries(this.exportAbleColumns).map(([key, label]) => ({
      key,
      label: `${label}`,
    }));
  }

  async ngOnInit() {
    this.paramMapSubscription = this.route
      .parent!.paramMap.pipe(
        combineLatestWith(
          defer(() => from(this.authService.currentUser)),
          this.clientEnvironmentService.items$,
        ),
      )
      .subscribe(async ([params, user, botEnvironment]) => {
        try {
          const corpId = params.get('corp');
          const hierarchyElementSystemName = params.get('hierarchyElementSystemName');
          const botCode = params.get('bot');
          if (!corpId || !hierarchyElementSystemName || !botCode || !user || !botEnvironment) {
            return;
          }
          const [corp, hierarchyElement, bot] = await firstValueFrom(
            this.corpsService
              .getCorpById(corpId)
              .pipe(
                combineLatestWith(
                  this.hierarchyElementsService.getHierarchyElement(`${corpId}-${hierarchyElementSystemName}`),
                  this.botsService.getBotBy(corpId, hierarchyElementSystemName, botCode, botEnvironment),
                ),
              ),
          );

          if (!corp || !hierarchyElement || !bot) {
            return;
          }

          this.corp = corp;
          this.hierarchyElement = hierarchyElement;
          this.bot = bot;

          this.refreshUI();

          const nodes = await firstValueFrom(this.nodesService.getNodesByBotId(this.bot.id));

          this.nodes = nodes.map(node => {
            makeObjectFirebaseTimestampHumanReadable(node);
            return Object.entries(node).reduce((nodeDetail: Partial<NodeModel>, [key, value]) => {
              if (typeof value === 'object') {
                value = JSON.stringify(value);
              }
              let objectToReduceToNode = { [key]: value };
              if (key === 'templates' && node.templates) {
                const filterTemplates = this.filterTemplates(node.templates);
                objectToReduceToNode = {
                  templateSMS: filterTemplates(TemplateChannels.SMS),
                };
              }
              return { ...nodeDetail, ...objectToReduceToNode };
            }, {});
          }) as unknown as NodeModel[];
        } catch (error) {
          this.toaster.error(error);
        } finally {
          this.loading = false;
        }
      });
  }

  refreshUI() {
    this.headerService.setPageTitle(`${this.bot.label} Export`);
  }

  toggleColumnExport(columnKey: NodeExportKeys) {
    const columnIndex = this.selectedColumns.findIndex(column => columnKey === column);
    if (columnIndex === -1) {
      this.selectedColumns = [...this.selectedColumns, columnKey];
      return;
    }
    this.selectedColumns.splice(columnIndex, 1);
  }

  exportNodes() {
    const nodesHeader = this.selectedColumns.map(column => this.exportAbleColumns[column]).join(',');
    const nodesRows = this.nodes.map(node => this.selectedColumns.flatMap(column => this.replaceDelimiters(node[column])).join(',')).join('\n');
    const dataToExport = `Nodes\n${nodesHeader}\n${nodesRows}`;

    const blob = new Blob([dataToExport], {
      type: 'text/plain;charset=utf-8',
    });
    const time = new Date().toISOString();
    FileSaver.saveAs(blob, `${this.bot.id}-${time}.csv`);
  }

  private filterTemplates(templates: TemplateModel[]) {
    return (templateChannel: TemplateChannels): string => {
      return this.replaceDelimiters(
        templates
          .filter(({ channel }) => channel === templateChannel)
          .map(({ rawTemplate }) => rawTemplate)
          .join('|'),
      );
    };
  }

  private removeNewLines(inputString: string) {
    return inputString.replace(/(\r\n|\n|\r)/gm, '');
  }

  private replaceDelimiters(inputString: string): string {
    if (typeof inputString !== 'string') {
      return inputString;
    }
    return this.removeNewLines(inputString.split(',').join('<COMMA>'));
  }

  ngOnDestroy() {
    if (this.paramMapSubscription) {
      this.paramMapSubscription.unsubscribe();
    }
  }
}
